import { environment } from './environment';

/**
 * Hosts de API por domínio, confirmados via engenharia reversa de rede real
 * (DevTools) e do bundle JS de
 * https://dev-cliente.affix.com.br/affixbeneficios/ (24/08/2026). Nomeados
 * de forma explícita (urlApi*) para deixar claro a que domínio de negócio
 * cada host serve — os valores em si vêm de `environment.apiUrls` (trocam
 * por ambiente via file replacement no `angular.json`).
 */
export const urlApiClientes = environment.apiUrls.clientes;
export const urlApiAtendimento = environment.apiUrls.atendimento;
export const urlApiLaserchip = environment.apiUrls.laserchip;
export const urlApiWhatsapp = environment.apiUrls.whatsapp;
export const urlApiSalvavidas = environment.apiUrls.salvavidas;
export const urlApiPrecadastro = environment.apiUrls.precadastro;
export const urlApiArquivos = environment.apiUrls.arquivos;
// Backend novo (portal-cliente-v2-backend) — ver environment.model.ts.
export const urlApiPortalClienteV2 = environment.apiUrls.portalClienteV2;

/**
 * Endpoints REST confirmados (via leitura do bundle minificado) ou
 * placeholder (marcados `// TODO`), agrupados por domínio/host acima.
 */
export const endpoints = {
  administradora: {
    // Confirmados lendo o bundle minificado real (24/08/2026). Recebem
    // {tenantSlug} e um segundo parâmetro fixo observado como "1" (não
    // confirmamos o que esse "1" representa — possivelmente um tipo/versão
    // de configuração). Host: urlApiLaserchip.
    parametrosResumidos: (tenantSlug: string) => `Administradora/ParametrosResumidos/${tenantSlug}/1`,
    parametrosCompletos: (tenantSlug: string) => `Administradora/ParametrosCompletos/${tenantSlug}/1`,
  },
  // CONFIRMADO em 26/08/2026 (homologação): host urlApiLaserchip, chamado
  // logo depois de `PossuiSenhaExterna`/`LoginExternoResumido`, ainda no
  // PASSO 1 (antes de digitar a senha) — usando o `partnerToken` estático.
  // Resposta: `{ token, segundosRestantes }`, onde `token` é um JWT NOVO e
  // de curta duração (~2h, `primarysid: "-2"` — diferente do
  // `partnerToken`, que tem `primarysid: "2"`). Esse token de SESSÃO é o
  // que precisa ser usado no `Authorization` do passo 2 (`LoginExterno`),
  // não o `partnerToken` — ver core/models/auth.model.ts e
  // core/store/effects.ts.
  token: {
    atualizarTokenSessao: 'Token/AtualizarTokenSessao',
  },
  // Padrão de URL CONFIRMADO via rede real (tenant conectaplan, 24/08/2026),
  // host urlApiArquivos. `idTipo` observado como "5" para logo e imagem de
  // fundo do login (significado não confirmado). Usado por
  // `AdministradoraService.resolveArquivoUrl` sempre que `useV2Boot` é
  // `false` (fluxo legado, o padrão hoje) — CONFIRMADO em 27/08/2026 que o
  // app real ainda depende desta resolução nesse fluxo (ver comentário
  // completo em administradora.service.ts).
  arquivos: {
    url: (idTipo: number, arquivoId: string) => `Arquivo/URL/${idTipo}/${arquivoId}`,
  },
  clientes: {
    // CONFIRMADO via rede real (tenant conectaplan, 24/08/2026): é isto que
    // o botão "Próximo" chama de verdade após CPF/CNPJ + data de nascimento
    // — o nome bate com a mensagem exibida em seguida ("Verificamos que você
    // já possui uma senha de acesso"). Contrato de request/response não
    // inspecionado (evitamos ler o payload por conter dados pessoais reais
    // do usuário que testou o fluxo) — mantido com o mesmo formato do
    // `IdentificarRequest` já usado (best effort).
    possuiSenhaExterna: 'PossuiSenhaExterna',
    // CONFIRMADO em 26/08/2026 (homologação): roda DEPOIS de
    // `possuiSenhaExterna`, ainda no PASSO 1 (mesmo request, `partnerToken`
    // estático) — resposta `{ codResultado, sucesso, mensagem }` (mesmo
    // formato de "resultado de negócio" do `LoginExterno`, ver
    // core/models/auth.model.ts). Parece ser uma pré-validação — o app real
    // chama e ignora o resultado se `PossuiSenhaExterna` já respondeu OK,
    // não achamos onde o resultado dela é usado de fato.
    loginExternoResumido: 'LoginExternoResumido',
    // CONFIRMADO em 26/08/2026 (homologação): PASSO 2 ("Entrar"). Usa o
    // TOKEN DE SESSÃO de `Token/AtualizarTokenSessao` no `Authorization`,
    // não o `partnerToken` estático — ver comentário acima e
    // core/models/auth.model.ts.
    loginExterno: 'LoginExterno',
    // CONFIRMADO em 27/08/2026 (homologação, tenant conectaplan), método
    // POST (verificado interceptando `XMLHttpRequest.prototype.open` no
    // navegador — não é GET como palpite inicial). Chamada SEM path params;
    // `iCodSegurado` vai no CORPO junto com `sCPF`/`sDtNascimento`/
    // `iCodAdm` (mesmos dados já guardados em `identificacaoRequest` do
    // slice `core`, ver FinanceiroService.listarTitulosPendentes). Alimenta
    // o widget "Boleto 2ª Via" (Home e card de mesmo nome em Financeiro).
    listarTitulosPendentesCodigoSegurado: 'ListarTitulosPendentesCodigoSegurado',
    // CONFIRMADO em 27/08/2026 — card "Histórico Pagamento" (Financeiro).
    // Método POST (mesma verificação acima), corpo é o `UraLog` já usado no
    // login (`{ iSeqUraLog: 0, iSeqInterface: 2 }`, ver auth.model.ts).
    listarRecebimentos: (iCodSegurado: number) => `ListarRecebimentos/${iCodSegurado}`,
    // CONFIRMADO em 27/08/2026 — card "Extrato Coparticipação" (Financeiro).
    listarAnoMesCoparticipacao: (iCodSegurado: number) =>
      `ListarAnoMesCoparticipacao/${iCodSegurado}`,
    // CONFIRMADO em 27/08/2026 — card "Demonstrativo de Pagamento"
    // (Financeiro), popula o dropdown "Selecione um ano". O endpoint do
    // botão "Gerar Relatório" NÃO foi capturado (não clicado de propósito
    // — geraria/baixaria um arquivo real).
    listarAnoInformePagamento: (iCodSegurado: number) => `ListarAnoInformePagamento/${iCodSegurado}`,
  },
  // Hosts confirmados no bundle (urlApiAtendimento, urlApiWhatsapp,
  // urlApiSalvavidas, urlApiPrecadastro) sem nenhum endpoint específico
  // mapeado ainda — as telas que os usam não foram analisadas.
  // TODO: mapear endpoints reais quando as telas correspondentes forem
  // analisadas.
};
